import type { W_FieldReportAssignmentHistory_FindForFieldReport_VM } from 'ab-api/web/fieldReportAssignmentHistory/findForFieldReport/viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { revisionCode } from '@acceligentllc/shared/utils/workOrderRevision';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import FieldReportHistoryAssignmentStatus from 'ab-enums/fieldReportHistoryAssignment.enum';

export class FieldReportHistoryRowFactory {
	static make(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM): FieldReportHistoryRow {
		switch (historyRowRecord.status) {
			case FieldReportHistoryAssignmentStatus.INITIAL:
				return new InitialPublishFieldReportHistoryRow(historyRowRecord);
			case FieldReportHistoryAssignmentStatus.REMOVED:
				return new AssigneeRemovedFieldReportHistoryRow(historyRowRecord);
			case FieldReportHistoryAssignmentStatus.REVISION:
				return new RepublishedFieldReportHistoryRow(historyRowRecord);
			default:
				throw new Error('Cannot create row for the type of status in history.');
		}
	}
}

abstract class FieldReportHistoryRow {
	protected assigneeFirstName: string;
	protected assigneeLastName: string;
	/** ISO format */
	protected timestamp: string;

	constructor(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM) {
		this.assigneeFirstName = historyRowRecord.assigneeFirstName;
		this.assigneeLastName = historyRowRecord.assigneeLastName;
		this.timestamp = historyRowRecord.createdAt;
	}

	getAssignee(): string {
		return `${this.assigneeFirstName} ${this.assigneeLastName}`;
	}

	getTimestamp(): string {
		return TimeUtils.formatDate(this.timestamp, TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME);
	}

	getISOstring(): string {
		return TimeUtils.formatDate(this.timestamp, TimeFormat.ISO_DATETIME, TimeFormat.ISO_DATETIME);
	}

	abstract getReason(): string;
}

class InitialPublishFieldReportHistoryRow extends FieldReportHistoryRow {

	constructor(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM) {
		super(historyRowRecord);
	}

	getReason(): string {
		return 'Initial Work Order publish';
	}
}

class AssigneeRemovedFieldReportHistoryRow extends FieldReportHistoryRow {

	private actionDoneByUserFirstName: Nullable<string>;
	private actionDoneByUserLastName: Nullable<string>;
	private actionDoneByFallback: string;
	private revision: number;

	constructor(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM) {
		super(historyRowRecord);

		if (!historyRowRecord.revision) {
			throw new Error('Cannot show history row for this assignment event without the work order revision it was done in.');
		}

		this.actionDoneByUserFirstName = historyRowRecord.actionDoneByUserFirstName;
		this.actionDoneByUserLastName = historyRowRecord.actionDoneByUserLastName;
		this.actionDoneByFallback = historyRowRecord.actionDoneByFallback;
		this.revision = historyRowRecord.revision;
	}

	getReason(): string {
		if (this.actionDoneByUserFirstName && this.actionDoneByUserLastName) {
			return `Manually removed by ${this.actionDoneByUserFirstName} ${this.actionDoneByUserLastName} in Rev.${revisionCode(this.revision)}`;
		}

		return `Manually removed by ${this.actionDoneByFallback} in Rev.${revisionCode(this.revision)}`;
	}
}

class RepublishedFieldReportHistoryRow extends FieldReportHistoryRow {

	private revision: number;

	constructor(historyRowRecord: W_FieldReportAssignmentHistory_FindForFieldReport_VM) {
		super(historyRowRecord);

		if (!historyRowRecord.revision) {
			throw new Error('Cannot show history row for this assignment event without the work order revision it was done in.');
		}

		this.revision = historyRowRecord.revision;
	}

	getReason(): string {
		return `Revision republished (Rev.${revisionCode(this.revision)})`;
	}
}

